"use client";

import { useState } from "react";
import { ChevronLeft, ChevronRight, Linkedin, Twitter } from "lucide-react";
import Image from "next/image";

export function WhosComing() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const speakers = [
    {
      name: "Adam Tickell",
      title: "Vice Chancellor",
      organization: "University of Birmingham",
      image: "/adam-ticket.jpg",
      bio: "Driving international partnerships and innovation in higher education across Africa and the UK.",
      linkedin: "#",
      twitter: "#",
    },
    {
      name: "Professor Folasade Ogunsola",
      title: "Vice Chancellor",
      organization: "University of Lagos",
      image: "/prof-folasade-ogunsola.jpg",
      bio: "Pioneering academic excellence and research innovation at Nigeria's premier university.",
      linkedin: "#",
      twitter: "#",
    },
  ];

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % speakers.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + speakers.length) % speakers.length);
  };

  return (
    <section className="bg-muted/30 py-28 md:py-36 mt-12 md:mt-20">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <div className="mb-4 inline-block">
            <span className="rounded-full bg-secondary/20 px-4 py-2 text-sm font-semibold text-secondary">
              Featured Speakers
            </span>
          </div>
          <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl lg:text-5xl text-balance">
            Plenary Speakers
          </h2>
          <p className="text-lg text-muted-foreground text-pretty">
            Meet the distinguished leaders and innovators who will share their
            insights and shape the conversation at disruptED Africa Summit 2026
          </p>
        </div>

        <div className="relative mx-auto max-w-5xl">
          {/* Carousel Container */}
          <div className="overflow-hidden rounded-2xl">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {speakers.map((speaker, index) => (
                <div key={index} className="min-w-full">
                  <div className="overflow-hidden rounded-2xl border-2 border-secondary bg-card shadow-2xl">
                    <div className="grid gap-8 md:grid-cols-2">
                      {/* Image Section */}
                      <div className="relative aspect-[4/5] overflow-hidden md:aspect-auto md:min-h-[500px] max-h-[700px]">
                        <Image
                          src={speaker.image || "/edukatelogo.png"}
                          alt={speaker.name}
                          fill
                          sizes="(min-width: 768px) 50vw, 90vw"
                          className="object-cover object-center"
                          priority={index === 0}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent" />
                      </div>

                      {/* Content Section */}
                      <div className="flex flex-col justify-center p-8 md:p-10">
                        <div className="mb-3 inline-block">
                          <span className="rounded-full bg-secondary px-4 py-1.5 text-sm font-semibold text-secondary-foreground">
                            Plenary Speaker
                          </span>
                        </div>
                        <h3 className="mb-2 text-3xl font-bold text-card-foreground md:text-4xl text-balance">
                          {speaker.name}
                        </h3>
                        <p className="mb-1 text-lg font-semibold text-secondary">
                          {speaker.title}
                        </p>
                        <p className="mb-6 text-base font-medium text-muted-foreground">
                          {speaker.organization}
                        </p>
                        <p className="mb-8 text-base leading-relaxed text-muted-foreground text-pretty">
                          {speaker.bio}
                        </p>
                        <div className="flex gap-3">
                          <a
                            href={speaker.linkedin}
                            className="flex h-11 w-11 items-center justify-center rounded-lg border-2 border-secondary bg-background transition-all hover:bg-secondary hover:text-secondary-foreground"
                            aria-label="LinkedIn"
                          >
                            <Linkedin className="h-5 w-5" />
                          </a>
                          <a
                            href={speaker.twitter}
                            className="flex h-11 w-11 items-center justify-center rounded-lg border-2 border-secondary bg-background transition-all hover:bg-secondary hover:text-secondary-foreground"
                            aria-label="Twitter"
                          >
                            <Twitter className="h-5 w-5" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border-2 border-secondary bg-background shadow-lg transition-all hover:bg-secondary hover:text-secondary-foreground disabled:opacity-50"
            aria-label="Previous speaker"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border-2 border-secondary bg-background shadow-lg transition-all hover:bg-secondary hover:text-secondary-foreground disabled:opacity-50"
            aria-label="Next speaker"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* Dots Indicator */}
          <div className="mt-8 flex justify-center gap-2">
            {speakers.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2.5 rounded-full transition-all ${
                  index === currentIndex
                    ? "w-8 bg-secondary"
                    : "w-2.5 bg-muted-foreground/30"
                }`}
                aria-label={`Go to speaker ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Organizations Grid */}
        <div className="mt-20">
          <h3 className="mb-8 text-center text-2xl font-bold text-foreground">
            Confirmed Organizations & Institutions
          </h3>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-border bg-card p-6 text-center transition-all hover:border-secondary hover:shadow-lg">
              <p className="font-semibold text-card-foreground">
                Ministry of Education, Nigeria
              </p>
              <p className="mt-2 text-sm font-medium text-secondary">
                Government Partner
              </p>
              <p className="mt-3 text-sm text-muted-foreground">
                Leading policy discussions on education reform
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-center transition-all hover:border-secondary hover:shadow-lg">
              <p className="font-semibold text-card-foreground">
                Leading African Universities
              </p>
              <p className="mt-2 text-sm font-medium text-secondary">
                Academic Partners
              </p>
              <p className="mt-3 text-sm text-muted-foreground">
                Showcasing partnership opportunities
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-center transition-all hover:border-secondary hover:shadow-lg">
              <p className="font-semibold text-card-foreground">
                Top EdTech Companies
              </p>
              <p className="mt-2 text-sm font-medium text-secondary">
                Innovation Partners
              </p>
              <p className="mt-3 text-sm text-muted-foreground">
                Demonstrating cutting-edge solutions
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-center transition-all hover:border-secondary hover:shadow-lg">
              <p className="font-semibold text-card-foreground">
                International Organizations
              </p>
              <p className="mt-2 text-sm font-medium text-secondary">
                Development Partners
              </p>
              <p className="mt-3 text-sm text-muted-foreground">
                Supporting sustainable education initiatives
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
